/**
 * /checkin/verify — Phone verification (Phase 1.5).
 *
 * A calm mobile-first two-step flow:
 *   Step 1: phone entry + "Send code"
 *   Step 2: 6-digit code + "Verify"
 * On success: pushes user forward to profile extras (DOB / country / TZ).
 *
 * Lockout-safe behaviour:
 *   · When the SMS provider is in DEV / unconfigured mode, users are
 *     never forced into an unverifiable flow — a support message is
 *     shown instead.
 *   · When admin has disabled `require_phone_verification`, users are
 *     redirected straight to their `from=` destination.
 *   · "Remind me later" respects the grace window and is disabled when
 *     grace is exhausted (with a clear explanation).
 *   · `from=` is validated to prevent open-redirect attacks.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckInShell, CHECKIN_PALETTE as P } from "./_shell";
import { checkinApi, getToken } from "@/lib/checkinApi";

/** Internal-redirect guard — only same-origin paths under /checkin/ or / are allowed. */
function safeInternalPath(raw) {
  if (!raw || typeof raw !== "string") return "/checkin/home";
  // Reject absolute URLs (http, https, //, mailto:, javascript:, data:, etc.)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw) || raw.startsWith("//")) {
    return "/checkin/home";
  }
  // Must start with a single "/" and stay within known internal areas.
  if (!raw.startsWith("/")) return "/checkin/home";
  const path = raw.split(/[?#]/)[0];
  if (path === "/" || path.startsWith("/checkin/") ||
      path.startsWith("/kape/preview")) {
    return raw;
  }
  return "/checkin/home";
}

export default function CheckInVerifyPhone() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(null);
  const [providerMode, setProviderMode] = useState("");
  const [requireVerification, setRequireVerification] = useState(true);
  const [checked, setChecked] = useState(false);

  const backTarget = useMemo(
    () => safeInternalPath(new URLSearchParams(location.search).get("from")),
    [location.search],
  );

  useEffect(() => {
    if (!getToken()) {
      navigate(`/checkin/signin?next=${encodeURIComponent("/checkin/verify")}`);
      return;
    }
    checkinApi.verifyStatus().then((s) => {
      setStatus(s);
      setProviderMode(s?.provider_mode || "");
      const require = !!(s?.config?.require_phone_verification);
      setRequireVerification(require);
      // Case A — verification disabled by admin OR user already verified:
      //           push to backTarget immediately.
      if (!require || s?.profile?.verified_phone) {
        if (s?.profile && (!s.profile.date_of_birth
                            || !s.profile.country
                            || !s.profile.time_zone)) {
          navigate("/checkin/profile-extras", { replace: true });
        } else {
          navigate(backTarget, { replace: true });
        }
        return;
      }
      setChecked(true);
    }).catch(() => { setChecked(true); });
  }, [navigate, backTarget]);

  const gate = status?.conversation_gate || {};
  const graceEnabled = !!(status?.config?.grace_period_enabled);
  const graceMax = Math.max(0, gate.grace_max || 0);
  const graceUsed = Math.max(0, gate.logins || 0);
  const graceRemaining = Math.max(0, graceMax - graceUsed);
  const graceExhausted = graceEnabled && graceRemaining <= 0;
  const providerUnavailable = providerMode !== "twilio";

  const send = async () => {
    setError(""); setNote("");
    if (!phone.trim()) { setError("Please enter your mobile number."); return; }
    setBusy(true);
    try {
      const res = await checkinApi.verifyRequestCode(phone.trim());
      // Never expose that the code was written to a backend log.
      if (res.mode === "twilio") {
        setStep(2);
        setNote("We just sent a 6-digit code to your phone.");
      } else {
        // Dev / unconfigured provider — no SMS was actually delivered.
        // Do NOT advance to the code screen, and do NOT tell the user
        // to look at a backend log.
        setError(
          "SMS verification is temporarily unavailable. Please try again "
          + "later or contact support at hello@kissedbycvon.com."
        );
      }
    } catch (e) {
      if (e.code === "phone_claimed") {
        setError("This phone number is already used by another account. Our team will review — we won't merge without your consent.");
      } else if (e.code === "invalid_phone") {
        setError("That number doesn't look quite right. Try again with country code, e.g. +1 415 555 2671.");
      } else if (e.code === "rate_limited") {
        setError("You've requested too many codes recently. Please wait a bit before trying again.");
      } else {
        setError(e.message || "Couldn't send the code. Try again.");
      }
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setError(""); setNote("");
    if (!code.trim()) { setError("Enter the 6-digit code we sent you."); return; }
    setBusy(true);
    try {
      await checkinApi.verifyConfirmCode(phone.trim(), code.trim());
      navigate("/checkin/profile-extras", { replace: true });
    } catch (e) {
      if (e.code === "invalid_code") {
        setError("That code didn't match. Please try again or request a new one.");
      } else if (e.code === "too_many_attempts") {
        setError("Too many attempts on this code. Send yourself a new one.");
      } else if (e.code === "challenge_expired" || e.code === "no_challenge") {
        setError("Your code expired. Send a new one.");
        setStep(1);
      } else {
        setError(e.message || "We couldn't verify that code. Try again.");
      }
    } finally { setBusy(false); }
  };

  const remindLater = () => {
    if (graceExhausted) return;
    navigate(backTarget);
  };

  if (!checked) return null;

  return (
    <CheckInShell title="Verify your phone" testid="checkin-verify-page">
      <div className="pt-2">
        <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
          Phase 1.5 · Trust & Identity
        </div>
        <h1 className="text-2xl mt-1 font-semibold" data-testid="verify-headline">
          {step === 1 ? "Help us secure your Check In account." : "Enter your 6-digit code"}
        </h1>
        <p className="text-sm mt-2" style={{ color: "#5F5766" }}>
          {step === 1
            ? "We've added mobile verification to better protect your account, preserve your conversation history, and secure your journey with Dr. C."
            : "Your code arrives by SMS in under a minute. It expires in 10 minutes."}
        </p>

        {gate.state === "grace_active" && !graceExhausted && (
          <div className="mt-4 rounded-xl px-3 py-2 text-xs"
               style={{ background: "#FDF6E4", color: "#8B6B14",
                        border: "1px solid #F1E3B0" }}
               data-testid="grace-banner">
            You can keep browsing your history for {graceRemaining} more sign-in{graceRemaining === 1 ? "" : "s"} before phone verification is required.
          </div>
        )}
        {graceExhausted && (
          <div className="mt-4 rounded-xl px-3 py-2 text-xs"
               style={{ background: "#FEEEF1", color: P.danger,
                        border: `1px solid ${P.danger}22` }}
               data-testid="grace-exhausted-banner">
            You&apos;ve used all your reminder bypasses. Phone verification is now required to keep going.
          </div>
        )}

        {providerUnavailable && requireVerification && (
          <div className="mt-4 rounded-xl px-3 py-3 text-sm"
               style={{ background: "#FEEEF1", color: P.danger,
                        border: `1px solid ${P.danger}22` }}
               data-testid="provider-unavailable-panel">
            SMS verification is temporarily unavailable. Please try again later, or email
            {" "}<a href="mailto:hello@kissedbycvon.com" style={{ textDecoration: "underline" }}>
              hello@kissedbycvon.com
            </a>{" "}for help.
          </div>
        )}

        {step === 1 ? (
          <div className="mt-6 space-y-3">
            <label className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
              Mobile phone
            </label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)}
                   type="tel" inputMode="tel" autoComplete="tel"
                   placeholder="+1 415 555 2671"
                   data-testid="verify-phone-input"
                   className="w-full px-3 py-3 rounded-xl text-base"
                   style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
            <button onClick={send} disabled={busy || providerUnavailable}
                    data-testid="verify-send-btn"
                    className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-60"
                    style={{ background: P.purple }}>
              {busy ? "Sending…" : "Send verification code"}
            </button>
            <button onClick={remindLater}
                    disabled={graceExhausted}
                    className="w-full py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: P.purple }}
                    data-testid="verify-later-btn"
                    title={graceExhausted ? "You've used all your reminder bypasses. Please verify to continue." : ""}>
              {graceExhausted ? "Verification required" : "Remind me later"}
            </button>
            {graceEnabled && !graceExhausted && (
              <div className="text-[11px] text-center" data-testid="grace-remaining"
                   style={{ color: "#5F5766" }}>
                {graceRemaining} of {graceMax} bypass{graceMax === 1 ? "" : "es"} remaining.
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <label className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
              6-digit code
            </label>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   inputMode="numeric" autoComplete="one-time-code"
                   placeholder="123456"
                   data-testid="verify-code-input"
                   className="w-full px-3 py-3 rounded-xl text-xl tracking-widest font-mono text-center"
                   style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
            <button onClick={confirm} disabled={busy || code.length < 4}
                    data-testid="verify-confirm-btn"
                    className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-60"
                    style={{ background: P.purple }}>
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button onClick={() => { setStep(1); setCode(""); }}
                    className="w-full py-2 text-sm"
                    style={{ color: P.purple }}
                    data-testid="verify-resend-btn">
              Use a different number
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 text-sm p-3 rounded-xl"
               style={{ background: "#FEEEF1", color: P.danger, border: `1px solid ${P.danger}22` }}
               data-testid="verify-error">
            {error}
          </div>
        )}
        {note && (
          <div className="mt-3 text-xs" style={{ color: "#5F5766" }} data-testid="verify-note">
            {note}
          </div>
        )}
      </div>
    </CheckInShell>
  );
}
